import React, {useState, useEffect, ChangeEvent} from 'react';
import axios from 'axios';
import {
    Container, Typography, Box, Paper, Divider, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, IconButton, Link
} from '@mui/material';
import { Edit, Delete, Star, Link as LinkIcon } from '@mui/icons-material';
import MediaUploader from '../components/MediaUploader';
import Grid from "@mui/material/Grid";
import {Comment, Subscriber} from "../types";
interface UploadedMedia {
    media_id: string;
    file_path: string; // The physical asset backdrop background
    redirect_url: string | null; // The clickable tracking website landing page action
    mime_type: string;
    lifetime_minutes: number | null;
    created_at: string;
    use_as_default?: boolean | number | null;
}
export default function MediaPage() {
    const [mediaItems, setMediaItems] = useState<UploadedMedia[]>([]);

    const [title, setTitle] = useState<string>('');
    const [body, setBody] = useState<string>('');
    const [image, setImage] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [previewCover, setPreviewCover] = useState<string | null>(null);
    const [fileType, setFileType] = useState<string | null>(null);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [IsUploadingMedia, setIsUploadingMedia] = useState<boolean>(false);
    const [editingMedia, setEditingMedia] = useState<UploadedMedia | null>(null);

    const poppins = { fontFamily: "'Poppins', sans-serif" };



    const fetchMedia = async () => {
        try {
            const res = await axios.get('https://api.237showbiz.com/api/media');
            setMediaItems(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Fetch error:", err);
        }
    };

    useEffect(() => {
        fetchMedia();
    }, []);

    const handleCoverImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // This is the critical line that was likely missing or disconnected
            setImage(file);

            // Update technical metadata for the preview
            setFileType(file.type);
            setPreviewCover(URL.createObjectURL(file));

            console.log("File captured for Split-Advert:", file.name);
        }
    };
    const fetchData = async () => {
        try {


            // Fetch current media registry items from backend controller
            const mediaRes = await axios.get<UploadedMedia[]>('https://api.237showbiz.com/api/media');



            // Synchronize local states with explicit backend verification checks
            setMediaItems(Array.isArray(mediaRes.data) ? mediaRes.data : []);
        } catch (err) {
            console.error("Dashboard Fetch Registry Sync Error:", err);
        }
    };
    const handleDeleteMedia = async (mediaId: string) => {
        if (!window.confirm("Permanently delete this media?")) return;
        try {
            await axios.delete('https://api.237showbiz.com/api/media', { data: { media_id: mediaId } });
            alert("Deleted successfully");
            fetchData();
        } catch (err) { alert("Delete failed"); }
    };

    const handleClearMedia = () => {
        if (previewCover) {
            URL.revokeObjectURL(previewCover);
        }
        setImage(null);
        setFileType(null);
        setPreviewCover(null);
    };
    const handleEditMedia = (item: UploadedMedia) => {
        setEditingMedia(item);
        setPreviewCover(item.file_path); // Set preview to existing path
        setFileType(item.mime_type);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleMediaSubmitAction = async (
        payload: {
            mediaUrl: string | null;
            lifetimeMinutes: number | null;
            isDefault: boolean; // Updated to include this
        }
    ) => {
        setIsUploadingMedia(true);

        try {
            if (editingMedia) {
                // =========================
                // UPDATE EXISTING (PUT)
                // =========================
                console.log("Updating existing media:", editingMedia.media_id);

                const response = await axios.put("https://api.237showbiz.com/api/media", {
                    media_id: editingMedia.media_id,
                    redirect_url: payload.mediaUrl,
                    lifetime_minutes: payload.lifetimeMinutes,
                    // If your backend handles setting/unsetting default via PUT:
                    use_as_default: payload.isDefault
                });
                console.log(response.data)

                if (response.data.success) {
                    alert("✅ Advert updated successfully!");
                    setEditingMedia(null);
                } else {
                    throw new Error(response.data.error || "Update failed.");
                }

            } else {
                // =========================
                // CREATE NEW (POST)
                // =========================
                if (!image && !payload.mediaUrl) {
                    alert("Please upload a file or provide a redirect URL.");
                    return;
                }

                const formData = new FormData();
                if (image instanceof File) formData.append("image", image);
                if (payload.mediaUrl) formData.append("redirect_url", payload.mediaUrl.trim());
                if (payload.lifetimeMinutes !== null) formData.append("lifetime_minutes", payload.lifetimeMinutes.toString());

                // Append the boolean as a string (FormData converts all values to strings)
                formData.append("use_as_default", payload.isDefault ? "1" : "0");

                const response = await axios.post("https://api.237showbiz.com/api/media", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                console.log(response.data)
                if (response.data.success) {
                    alert("✅ Advert uploaded successfully!");
                    handleClearMedia();
                } else {
                    throw new Error(response.data.error || "Upload failed.");
                }
            }

            await fetchData();

        } catch (error: any) {
            console.error("Submission error:", error);
            alert(error?.response?.data?.error || error.message || "An error occurred.");
        } finally {
            setIsUploadingMedia(false);
        }
    };
    const handleUpdateMediaWrapper = (
        mediaId: string,
        payload: {
            redirect_url?: string;
            lifetime_minutes?: number;
            is_default?: boolean // 1. Add this to the payload type
        }
    ) => {
        // 2. Pass the is_default from the payload, defaulting to false if undefined
        handleMediaSubmitAction({
            mediaUrl: payload.redirect_url || null,
            lifetimeMinutes: payload.lifetime_minutes ?? null,
            isDefault: payload.is_default ?? false
        });
    };

    return (
        <Container sx={{ py: 4 }}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 800, ...poppins }}>Media Management</Typography>

            <Grid size={{ xs: 12 }}>
                <MediaUploader
                    preview={previewCover}
                    fileType={fileType}
                    onImageChange={handleCoverImageChange}
                    onClearMedia={handleClearMedia}
                    onSubmitMedia={handleMediaSubmitAction}
                    onUpdateMedia={handleUpdateMediaWrapper}
                    // FIX: Map the database object to the expected component format
                    currentMedia={editingMedia ? {
                        media_id: editingMedia.media_id,
                        redirect_url: editingMedia.redirect_url ?? undefined,
                        lifetime_minutes: editingMedia.lifetime_minutes ?? undefined,
                        use_as_default: !!editingMedia.use_as_default // Forces number/null to boolean
                    } : null}
                    onDeleteMedia={handleDeleteMedia}
                    poppins={poppins}
                    isUploadingMedia={IsUploadingMedia}
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #E9EDF7', overflow: 'hidden', bgcolor: 'white' }}>
                    <Box p={4} display="flex" alignItems="center" gap={2}>
                        <Box sx={{ p: 1, bgcolor: '#F0F9FF', borderRadius: '10px' }}>
                            <LinkIcon sx={{ color: '#0EA5E9' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B2559', ...poppins }}>Live Media Link Registry</Typography>
                    </Box>
                    <Divider />
                    <TableContainer sx={{ maxHeight: 320 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>MEDIA ID</TableCell>
                                    <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>DEFAULT</TableCell>
                                    <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>SOURCE</TableCell>
                                    <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>REDIRECT</TableCell>
                                    <TableCell align="center" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {mediaItems.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#A3AED0', ...poppins }}>No media logs loaded.</TableCell>
                                    </TableRow>
                                ) : (
                                    mediaItems.map((item) => (
                                        <TableRow key={item.media_id} hover>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', ...poppins }}>
                                                {item.media_id.substring(0, 8)}...
                                            </TableCell>

                                            {/* NEW: Default Indicator Column */}
                                            <TableCell>
                                                {item.use_as_default ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'orange' }}>
                                                        <Star fontSize="small" />
                                                        <Typography variant="caption" sx={{ fontWeight: 800 }}>DEFAULT</Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" sx={{ color: '#A3AED0' }}>-</Typography>
                                                )}
                                            </TableCell>

                                            <TableCell>
                                                <Box sx={{ width: 60, height: 40, borderRadius: '6px', overflow: 'hidden', bgcolor: '#F4F7FE' }}>
                                                    {item.mime_type.startsWith('image/') ? (
                                                        <img src={item.file_path} alt="media" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <video src={item.file_path} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                                                    )}
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={poppins}>
                                                {item.redirect_url ? (
                                                    <Link href={item.redirect_url} target="_blank" rel="noreferrer" sx={{ color: '#0EA5E9', fontWeight: 600, textDecoration: 'none' }}>
                                                        {item.redirect_url.substring(0, 20)}...
                                                    </Link>
                                                ) : <Typography variant="caption" sx={{ color: '#A3AED0' }}>None</Typography>}
                                            </TableCell>

                                            <TableCell align="center">
                                                <IconButton color="primary" onClick={() => handleEditMedia(item)} size="small">
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => handleDeleteMedia(item.media_id)} size="small">
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>
        </Container>
    );
}